// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/crmService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import type {
  AtendimentoResumo,
  AtendimentoDetalhe,
  CriarAtendimentoRequest,
  AtualizarAtendimentoRequest,
  AdicionarContatoRequest,
  AtendimentoAnalitico,
  PaginadoResponse,
  Visao360,
} from '../types';

const BASE = '/api/crm';

export type OrdemAtendimentos = 'solicitacao' | 'numero' | 'assunto' | 'prioridade';

/**
 * Filtros server-side da listagem de atendimentos (datas "yyyy-MM-dd"). União da v118
 * (situacao, dtInicio/dtFim, ordenarPor/decrescente) com a paridade (status, de/ate,
 * cliente, prioridade) — o servidor aceita os dois nomes.
 */
export interface ListaAtendimentosFiltros {
  busca?: string;
  /** 'pendente' | 'concluido' ('todos' ou vazio = todos). */
  situacao?: string;
  /** Sinônimo de `situacao` (telas da paridade). */
  status?: 'pendente' | 'concluido';
  dtInicio?: string;
  dtFim?: string;
  /** Sinônimos de dtInicio/dtFim (telas da paridade). */
  de?: string;
  ate?: string;
  clienteId?: number;
  prioridadeId?: number;
  ordenarPor?: OrdemAtendimentos;
  decrescente?: boolean;
}

/** Nome usado pelas telas da paridade. */
export type FiltrosAtendimentos = ListaAtendimentosFiltros;

export interface AtendimentosResumo {
  total: number;
  pendentes: number;
  concluidos: number;
  urgentes: number;
}

/** Linha da listagem: o servidor já manda o nome do cliente. */
export type AtendimentoListagemItem = AtendimentoResumo & { clienteNome?: string | null };

/** Página + contadores do recorte filtrado (cards da tela). */
export interface AtendimentoListagem extends PaginadoResponse<AtendimentoListagemItem> {
  totalPendentes: number;
  totalConcluidos: number;
  totalUrgentes: number;
}

const paramsDoFiltro = (filtros: ListaAtendimentosFiltros) => ({
  busca: filtros.busca?.trim() || undefined,
  dtInicio: filtros.dtInicio || filtros.de || undefined,
  dtFim: filtros.dtFim || filtros.ate || undefined,
  clienteId: filtros.clienteId || undefined,
  prioridadeId: filtros.prioridadeId || undefined,
});

export const crmService = {
  /**
   * Lista paginada + contadores; busca (número, assunto ou cliente), situação, período da
   * solicitação (yyyy-MM-dd), cliente, prioridade e ordenação são feitos no servidor.
   */
  async getAll(
    pagina = 1,
    tamanhoPagina = 20,
    filtros: ListaAtendimentosFiltros = {},
  ): Promise<AtendimentoListagem> {
    const situacao = filtros.situacao ?? filtros.status;
    const { data } = await apiClient.get<AtendimentoListagem>(BASE, {
      params: {
        pagina, tamanhoPagina,
        ...paramsDoFiltro(filtros),
        situacao: situacao && situacao !== 'todos' ? situacao : undefined,
        ordenarPor: filtros.ordenarPor,
        decrescente: filtros.decrescente,
      },
    });
    return data;
  },

  /** KPIs do período/busca/cliente/prioridade (sem o filtro de situação). */
  async getResumo(filtros: ListaAtendimentosFiltros = {}): Promise<AtendimentosResumo> {
    const { data } = await apiClient.get<AtendimentosResumo>(`${BASE}/resumo`, {
      params: paramsDoFiltro(filtros),
    });
    return data;
  },

  async getById(id: number): Promise<AtendimentoDetalhe> {
    const { data } = await apiClient.get<AtendimentoDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarAtendimentoRequest): Promise<AtendimentoDetalhe> {
    const { data } = await apiClient.post<AtendimentoDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarAtendimentoRequest): Promise<AtendimentoResumo> {
    const { data } = await apiClient.put<AtendimentoResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  async adicionarContato(id: number, request: AdicionarContatoRequest): Promise<AtendimentoAnalitico> {
    const { data } = await apiClient.post<AtendimentoAnalitico>(`${BASE}/${id}/contatos`, request);
    return data;
  },

  async removerContato(atendimentoId: number, contatoId: number): Promise<void> {
    await apiClient.delete(`${BASE}/${atendimentoId}/contatos/${contatoId}`);
  },

  async visao360(clienteId: number): Promise<Visao360> {
    const { data } = await apiClient.get<Visao360>(`${BASE}/visao360/${clienteId}`);
    return data;
  },
};
